import { Box } from '@qbe/ads-core';
import React, { useEffect } from 'react';

import Loading from '~/components-ds/Loading';
import { RETRY_MESSAGE } from '~/constants/domain-specific';
import { TRIGGER_MFE_CREDIT_CARD_SUBMIT } from '~/constants/events';
import { useAppDispatch } from '~/hooks';
import { genericDecline } from '~/slices/decline';
import {
  addPaymentError,
  clearPaymentErrors,
  updateCreditCardName,
  updateCreditCardType,
  updateIsPaymentInProgress,
  updatePaymentState,
  updateTransientToken,
} from '~/slices/payments';
import { PaymentErrorCode } from '~/types/payments';
import { logError } from '~/utils/logError';
import { toCreditCardType } from '~/utils/toCreditCardType';

import { IFRAME_CONTAINER_ID, IFRAME_HEIGHT, MessageType } from './constants';
import { PaymentFormState } from './types';
import { usePaymentMFE } from './usePaymentMFE';

interface CreditCardIframeContainerProps {
  onFormStateChange: (state: PaymentFormState) => void;
  onSubmitPayment: () => Promise<unknown>;
}

const CreditCardIframeContainer = ({
  onFormStateChange,
  onSubmitPayment,
}: CreditCardIframeContainerProps) => {
  const dispatch = useAppDispatch();

  const { mfeInstanceRef, isLoading, formState, formData } = usePaymentMFE();
  const { transientToken, cardHolderName, cardType } = formData;

  useEffect(() => {
    // Notify parent component of form state changes
    onFormStateChange(formState);
  }, [formState, onFormStateChange]);

  // Handle errors
  useEffect(() => {
    if (formState.hasError) {
      if (!formState.errorCode) {
        dispatch(genericDecline(formState.errorMessage));
      }
      if (formState.errorCode === PaymentErrorCode.PAYMENT_ERROR_RETRY) {
        dispatch(updatePaymentState('error'));
        dispatch(
          addPaymentError({
            code: formState.errorCode,
            message: formState.errorMessage || RETRY_MESSAGE,
          })
        );
        dispatch(updateIsPaymentInProgress(false));
      }
      logError(new Error(formState.errorMessage));
    }
  }, [formState.hasError, formState.errorCode, formState.errorMessage, dispatch]);

  // Handle successful payment
  useEffect(() => {
    const handlePaymentData = async () => {
      if (transientToken && cardHolderName && cardType) {
        dispatch(updateTransientToken(transientToken));
        dispatch(updateCreditCardName(cardHolderName));
        dispatch(updateCreditCardType(toCreditCardType(cardType)));
        await onSubmitPayment();
        dispatch(updateIsPaymentInProgress(false));
      }
    };
    handlePaymentData();
  }, [transientToken, cardHolderName, cardType, dispatch, onSubmitPayment]);

  useEffect(() => {
    // Listen for the custom event to submit the credit card form
    const submitForm = () => {
      dispatch(clearPaymentErrors());
      mfeInstanceRef?.current?.postMessage({
        type: MessageType.SUBMIT_CC_FORM,
      });
      dispatch(updateIsPaymentInProgress(true));
    };

    window.addEventListener(TRIGGER_MFE_CREDIT_CARD_SUBMIT, submitForm);

    return () => {
      window.removeEventListener(TRIGGER_MFE_CREDIT_CARD_SUBMIT, submitForm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ width: '100%', maxWidth: '400px' }}>
      <div id={IFRAME_CONTAINER_ID} />
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: IFRAME_HEIGHT,
          }}
        >
          <Loading />
        </Box>
      )}
    </Box>
  );
};

export default CreditCardIframeContainer;
